import { useState, type ReactNode } from 'react';
import { DefaultPortalContext } from '@servicetitan/design-system';

interface PortalRootProviderProps {
  readonly children: ReactNode;
}

/**
 * anvil1 components (e.g. Toast.Group, Modal) portal to document.body by
 * default. AnvilProvider only adds its bare `.anvil2` class to body as a
 * compatibility shim - it never adds `theme-core`/`mode-*`, and anvil2's
 * design tokens are only defined under the compound `.anvil2.theme-core`
 * selector on AnvilProvider's own wrapper. Anvil2 components rendered
 * inside a body-portaled anvil1 container therefore see none of those
 * tokens and render unstyled. Redirecting the default portal target to a
 * node inside AnvilProvider's own DOM subtree keeps portaled content
 * within the scoped tree so this token lookup keeps working.
 *
 * Must be rendered inside <AnvilProvider> for the redirect target to land
 * in AnvilProvider's own scoped subtree.
 */
export function PortalRootProvider({ children }: PortalRootProviderProps) {
  const [portalRoot, setPortalRoot] = useState<HTMLDivElement | null>(null);

  return (
    <>
      {/* Typed as non-nullable, but the library's own default context
          value is null until this mounts - matches that. */}
      <DefaultPortalContext.Provider value={portalRoot as HTMLDivElement}>
        {children}
      </DefaultPortalContext.Provider>
      <div ref={setPortalRoot} />
    </>
  );
}
